import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Typography,
    CircularProgress,
    Stack,
    Autocomplete,
    TextField,
    List,
    ListItem,
    Divider,
    Container,
    Grid,
    Pagination,
} from '@mui/material';
import { apiService } from '../services/api';
import { EpisodeCard } from '../components/EpisodeCard';
import { PodcastSponsors } from '../components/PodcastSponsors';
import ProgressiveImage from '../components/common/ProgressiveImage';

interface Sponsor {
    _id: string;
    name: string;
    logo?: string;
    website?: string;
    categories?: string[];
    mentionCount: number;
    episodeCount: number;
}

interface Episode {
    _id: string;
    title: string;
    description?: string;
    duration?: number;
    publishedAt?: string;
    podcastId: string;
    sponsors?: Sponsor[];
    thumbnail?: string;
    url?: string;
    views?: string;
    likes?: string;
    comments?: string;
    source?: string;
}

const PodcastDetail = () => {
    const { t } = useTranslation('explorar');
    const { slug } = useParams();
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedSponsors, setSelectedSponsors] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const episodesPerPage = 8;

    // Fetch podcast data
    const {
        data: podcast,
        isLoading: podcastLoading,
        error: podcastError,
    } = useQuery({
        queryKey: ['podcast', slug],
        queryFn: () => apiService.getPodcastBySlug(slug || ''),
        staleTime: 0,
        enabled: !!slug,
    });

    // Fetch episodes with pagination
    const {
        data: episodesData,
        isLoading: episodesLoading,
        error: episodesError,
    } = useQuery({
        queryKey: ['podcast-episodes', podcast?._id, currentPage],
        queryFn: () => apiService.getPodcastEpisodes(podcast?._id || '', currentPage, episodesPerPage),
        enabled: !!podcast?._id,
        placeholderData: (previousData) => previousData,
        staleTime: 1000 * 60 * 2, // 2 min
    });

    // Fetch sponsors
    const { data: sponsors } = useQuery({
        queryKey: ['podcast-sponsors', podcast?._id],
        queryFn: () => apiService.getPodcastSponsors(podcast?._id || ''),
        enabled: !!podcast?._id,
        staleTime: 0,
    });

    // Filter sponsors based on selected categories and sponsor names
    const filteredSponsors = useMemo(() => {
        if (!sponsors) return [];
        return sponsors.filter((sponsor: Sponsor) => {
            // If no filters are selected, show all sponsors
            if (selectedCategories.length === 0 && selectedSponsors.length === 0) {
                return true;
            }

            let shouldInclude = false;

            // Check if sponsor matches selected categories
            if (selectedCategories.length > 0) {
                const matchesCategories = sponsor.categories && sponsor.categories.some(category => selectedCategories.includes(category));
                shouldInclude = shouldInclude || !!matchesCategories;
            }

            // Check if sponsor matches selected sponsor names
            if (selectedSponsors.length > 0) {
                const matchesSponsorName = selectedSponsors.includes(sponsor.name);
                shouldInclude = shouldInclude || matchesSponsorName;
            }

            return shouldInclude;
        });
    }, [sponsors, selectedCategories, selectedSponsors]);

    // Determine if any filters are active
    const hasActiveFilters = selectedCategories.length > 0 || selectedSponsors.length > 0 || searchTerm.length > 0;

    // Filter episodes based on selected sponsors and categories
    const filteredEpisodesBySponsors = useMemo(() => {
        const episodes = episodesData?.episodes || [];

        if (!episodes || !filteredSponsors) return episodes || [];

        // If no filters selected, return all episodes
        if (selectedCategories.length === 0 && selectedSponsors.length === 0) {
            return episodes;
        }

        console.log('filteredSponsors', filteredSponsors);

        // Get IDs of filtered sponsors
        const relevantSponsorIds = new Set(filteredSponsors.map((sponsor: Sponsor) => sponsor._id));

        // Filter episodes that mention any of the filtered sponsors
        const filteredEpisodes = episodes.filter((episode: Episode) => {
            const episodeSponsors = episode.sponsors || [];
            const hasRelevantSponsor = episodeSponsors.some(sponsor => relevantSponsorIds.has(sponsor._id));

            return hasRelevantSponsor;
        });

        return filteredEpisodes;
    }, [episodesData, filteredSponsors, selectedCategories, selectedSponsors]);

    const pagination = episodesData?.pagination;

    // Final episodes list with search filter
    const finalFilteredEpisodes = useMemo(() => {
        const baseEpisodes = filteredEpisodesBySponsors || [];

        // Filter by episode title
        if (searchTerm) {
            return baseEpisodes.filter((episode: Episode) =>
                episode.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return baseEpisodes;
    }, [filteredEpisodesBySponsors, searchTerm]);

    // Reset to page 1 when filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategories, selectedSponsors, searchTerm]);

    // Get unique sponsor names from filtered sponsors
    const sponsorOptions = useMemo(() => {
        if (!sponsors) return [];
        const uniqueSponsors = new Set<string>();
        sponsors.forEach((sponsor: Sponsor) => {
            uniqueSponsors.add(sponsor.name);
        });
        return Array.from(uniqueSponsors).sort();
    }, [sponsors]);

    // Extract unique categories from sponsors
    const uniqueCategories = useMemo(() => {
        if (!sponsors) return [];
        const categories = new Set<string>();
        sponsors.forEach((sponsor: Sponsor) => {
            if (sponsor.categories && sponsor.categories.length > 0) {
                sponsor.categories.forEach(category => {
                    categories.add(category);
                });
            }
        });
        return Array.from(categories).sort();
    }, [sponsors]);

    if (podcastLoading || episodesLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (podcastError || episodesError) {
        return (
            <Box sx={{ mt: 4 }}>
                <Typography color="error">
                    {t('podcastDetail.loadError')}{' '}
                    {JSON.stringify(podcastError || episodesError)}
                </Typography>
            </Box>
        );
    }

    if (!podcast) {
        return (
            <Box sx={{ mt: 4 }}>
                <Typography>{t('podcastDetail.notFound')}</Typography>
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ backgroundColor: 'white' }}>
            <Stack spacing={5} sx={{ pt: 2 }}>
                {/* Profile Header Section */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
                    {/* Profile Image */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            flexShrink: 0,
                            width: { xs: '100%', lg: 'auto' },
                        }}
                    >
                        <ProgressiveImage
                            src={(podcast.imageUrl || podcast.youtubeData?.snippet?.thumbnails?.high?.url) || ''}
                            alt={podcast.title}
                            eager={true}
                            sx={{
                                width: { xs: '100%', sm: '200px' },
                                height: { xs: 'auto', sm: '200px' },
                                aspectRatio: '1',
                                objectFit: 'cover',
                                maxHeight: '200px',
                                maxWidth: '200px',
                                borderRadius: '20px',
                            }}
                        />
                    </Box>

                    {/* Profile Info */}
                    <Box sx={{ flex: 1, pl: { xs: 0, lg: 3 }, pt: { xs: 3, lg: 0 } }}>
                        {/* Title */}
                        <Typography
                            variant="h1"
                            component="h1"
                            sx={{
                                fontSize: { xs: '2rem', sm: '2.5rem', lg: '3rem' },
                            }}
                        >
                            {podcast.title}
                        </Typography>

                        <Box sx={{ pb: 1 }}>
                            <Typography variant="body1" color="text.secondary">
                                {podcast.description}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Divider />

                <Box>
                    <Typography variant="h2">{t('podcastDetail.mentionsHeading')}</Typography>
                </Box>

                <Box sx={{ minHeight: '50vh', mt: 3 }}>
                    <Grid container spacing={5}>
                        <Grid size={{ xs: 12, md: 12 }}>
                            <Stack spacing={2}>
                                <Typography variant="h3">{t('podcastDetail.sponsors')}</Typography>
                                {/* Sponsor Filters */}
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Box>
                                            <Autocomplete
                                                multiple
                                                options={sponsorOptions}
                                                value={selectedSponsors}
                                                onChange={(_, newValue) =>
                                                    setSelectedSponsors(newValue)
                                                }
                                                renderInput={params => (
                                                    <TextField
                                                        {...params}
                                                        label={t('podcastDetail.filterSponsor')}
                                                        placeholder={t('podcastDetail.select')}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }} sx={{ ml: { xs: -2, sm: 0, md: 0 } }}>
                                        <Box>
                                            <Autocomplete
                                                multiple
                                                options={uniqueCategories}
                                                value={selectedCategories}
                                                onChange={(_, newValue) =>
                                                    setSelectedCategories(newValue)
                                                }
                                                renderInput={params => (
                                                    <TextField
                                                        {...params}
                                                        label={t('podcastDetail.sponsorCategories')}
                                                        placeholder={t('podcastDetail.select')}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/* Sponsors Table */}
                                <PodcastSponsors
                                    podcastId={podcast._id as string}
                                    selectedShowId={podcast._id}
                                    selectedShowTitle={podcast.title}
                                    selectedSponsorId={selectedSponsors.join(',')}
                                    sponsors={filteredSponsors}
                                />
                            </Stack>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Stack spacing={3}>
                                <Typography variant="h3">{t('podcastDetail.episodes')}</Typography>

                                <Stack spacing={2}>
                                    <TextField
                                        fullWidth
                                        label={t('podcastDetail.searchEpisode')}
                                        placeholder={t('podcastDetail.searchPlaceholder')}
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                    />

                                    {finalFilteredEpisodes && finalFilteredEpisodes.length > 0 ? (
                                        <>
                                            <List disablePadding>
                                                {finalFilteredEpisodes.map(
                                                    (episode: Episode, index: number) => (
                                                        <React.Fragment key={episode._id}>
                                                            <ListItem disablePadding>
                                                                <EpisodeCard
                                                                    episode={episode}
                                                                    podcast={podcast}
                                                                    selectedSponsors={filteredSponsors}
                                                                />
                                                            </ListItem>
                                                            {index < finalFilteredEpisodes.length - 1 && (
                                                                <Divider sx={{ my: '-0px', pt: -2 }} />
                                                            )}
                                                        </React.Fragment>
                                                    )
                                                )}
                                            </List>

                                            {/* Pagination Controls - Only show when no filters are active */}
                                            {!hasActiveFilters && pagination && (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                                                    <Stack spacing={2} alignItems="center">
                                                        {/* Only show pagination controls if more than 1 page */}
                                                        {pagination.totalPages > 1 && (
                                                            <Pagination
                                                                count={pagination.totalPages}
                                                                page={currentPage}
                                                                onChange={(_, page) => setCurrentPage(page)}
                                                                color="primary"
                                                                size="large"
                                                                showFirstButton
                                                                showLastButton
                                                                disabled={episodesLoading}
                                                            />
                                                        )}
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t('podcastDetail.showingRange', {
                                                                from: ((currentPage - 1) * episodesPerPage) + 1,
                                                                to: Math.min(currentPage * episodesPerPage, pagination.total),
                                                                total: pagination.total,
                                                            })}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            )}

                                            {/* Show filter info when filters are active */}
                                            {hasActiveFilters && (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {t('podcastDetail.showingFiltered', {
                                                            shown: finalFilteredEpisodes.length,
                                                            total: pagination?.total || 0,
                                                        })}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </>
                                    ) : (
                                        <Typography variant="body1" color="text.secondary">
                                            {episodesLoading ? t('podcastDetail.loadingEpisodes') : t('podcastDetail.noEpisodes')}
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>
                        </Grid>
                    </Grid>

                </Box>
            </Stack>
        </Container>
    );
};

export default PodcastDetail; 